import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useExternalHealth } from '../runs/queries.js'
import { DataProvenanceLine, DegradationBanner, TenantSwitcher } from '../ui/index.js'
import { useSelectTenant, useTenants } from '../tenants/queries.js'
import { useMe } from './queries.js'

/**
 * The session-aware shell every protected route sits behind. `useMe`'s three
 * states map onto three screens: still checking, genuinely broken, or signed
 * out — collapsing the last two would show a login form for a backend outage,
 * which sends the operator to the wrong fix.
 *
 * Identity is passed to children via `Outlet`'s context rather than a
 * separate provider — there is exactly one consumer shape (the authenticated
 * shell), so a context provider would be a second way to get the same value
 * `useMe` already holds.
 *
 * The degradation banner lives here, above every protected route, because
 * it's orthogonal to each surface's own state (addendum §A) — a Runs page
 * mid-loading and a degraded Zoho connection are two independent facts, not
 * one collapsed into the other.
 *
 * The provenance line sits beside it for the same reason and one more: it is
 * a statement about *every* screen, since every screen renders Cadence data
 * under the signed-in tenant's name. Putting it on Home alone would leave the
 * Runs and Detail surfaces making the same implicit claim unqualified.
 */
export function RequireAuth() {
  const location = useLocation()
  const { data, isPending, isError } = useMe()
  // `enabled: false` until `useMe` has actually resolved to a signed-in
  // user — the endpoint requires a session, and this hook always fires on
  // mount regardless of the early returns below.
  const externalHealth = useExternalHealth(!isPending && !isError && data !== null)
  const tenants = useTenants()
  const selectTenant = useSelectTenant()

  if (isPending) {
    return <p>Checking your session…</p>
  }

  if (isError) {
    return <p role="alert">Could not reach the console server. Try reloading.</p>
  }

  if (data === null) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  return (
    /**
     * ═══════════════════════════════════════════════════════════════════════
     * THE SHELL OWNS THE VIEWPORT — `LGH-016`.
     *
     * This was a fragment, so everything it renders above the route — the
     * degradation banner, the provenance line, the workspace block — was a
     * sibling of the page rather than something the page knew about. **Home
     * claimed `height: 100vh` and meant it**, so the document was
     * `chrome + 100vh` and scrolled on every route at every viewport.
     *
     * Measured before it was touched: `#root` held a **111px** chrome div and
     * a `main` of exactly `100vh`, and the document overflowed by **exactly
     * 111** at both 900 and 850. Arithmetic, not content — which is why the
     * queue-height explanation was never viable.
     *
     * So the shell takes the viewport and hands the route what is left.
     * **Deliberately no `overflow: hidden` here**: only Home clips itself, and
     * every other page is meant to grow and let the document scroll. This
     * changes which box owns the height, not who is allowed to scroll.
     * ═══════════════════════════════════════════════════════════════════════
     */
    <div
      style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}
      data-testid="app-shell"
    >
      {externalHealth.data && (
        <>
          <DegradationBanner health={externalHealth.data} />
          <DataProvenanceLine
            provenance={externalHealth.data.provenance}
            sessionTenantName={
              tenants.data?.tenants.find((t) => t.id === data.currentTenantId)?.displayName ??
              `workspace ${data.currentTenantId.slice(0, 8)}…`
            }
          />
        </>
      )}
      {/*
        THE WORKSPACE BLOCK IS ALWAYS RENDERED. It used to be
        `{tenants.data && <TenantSwitcher/>}`, so anything other than a
        successful fetch removed the control from the page with no error and
        no explanation — the founder watched it vanish and had to ask why. A
        control that disappears silently is worse than one that says it cannot
        load: the first reads as a product decision.

        Every non-success state gets a line, not just the failed one. React
        Query can also sit in `pending` indefinitely — it pauses fetches while
        it believes the browser is offline — and an indefinite pause looks
        exactly like the bug we just fixed. So the switch is on what we have,
        not on which of several ways we came to have nothing.
      */}
      <div style={{ padding: '0.75rem 1rem 0', flex: '0 0 auto' }}>
        {tenants.data ? (
          <TenantSwitcher
            tenants={tenants.data.tenants}
            excludedFixtures={tenants.data.excludedFixtures}
            currentTenantId={data.currentTenantId}
            pending={selectTenant.isPending}
            error={selectTenant.isError ? selectTenant.error.message : null}
            onSelect={(tenantId) => {
              selectTenant.mutate(tenantId)
            }}
          />
        ) : (
          <p
            role={tenants.isError ? 'alert' : undefined}
            style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--muted)' }}
          >
            {tenants.isError
              ? 'The workspace list could not be loaded, so no switcher is being shown. Everything below is still scoped to the workspace this session opened in.'
              : 'Loading the workspace list…'}
          </p>
        )}
      </div>
      {/*
        The route gets the remainder, as a definite height — which is what
        lets Home say `height: 100%` and mean "what is left" rather than
        `100vh` and mean "all of it". `min-height: 0` so a page that clips
        internally can actually shrink below its content.
      */}
      <div
        style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}
        data-testid="route-area"
      >
        <Outlet context={data} />
      </div>
    </div>
  )
}
